import React, { useState, useEffect } from "react";
import { FaUsers, FaEdit, FaTrash } from "react-icons/fa";
import { getFamilyMembers, removeFamilyMember } from "../api/family.api";
import { getStaffId, getContactNumber } from "../utils/storage";
import FamilyMemberModal from "./FamilyMemberModal";
import ConfirmModal from "../Popups/ConfirmModal";
import LoadingState from "./States/LoadingState";
import { toast } from "./Toast";

const ProfileFamilySection = () => {
  const staffId = getStaffId();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedMember, setSelectedMember] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const load = () => {
    if (!staffId) {
      setLoading(false);
      return;
    }
    getFamilyMembers(staffId)
      .then((res) => setMembers(res.data.family_members || []))
      .catch(() => setError("Error fetching family members"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [staffId]);

  const openCreate = () => {
    setSelectedMember(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openView = (member) => {
    setSelectedMember(member);
    setModalMode("view");
    setModalOpen(true);
  };

  const handleSaved = (member, action) => {
    if (action === "create") setMembers((prev) => [...prev, member]);
    else setMembers((prev) => prev.map((m) => (m._id === member._id ? member : m)));
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    removeFamilyMember(staffId, pendingDeleteId)
      .then(() => {
        setMembers((prev) => prev.filter((m) => m._id !== pendingDeleteId));
        toast.success("Family member removed successfully.");
      })
      .catch(() => {
        setError("Error removing family member");
        toast.error("Error removing family member");
      })
      .finally(() => setPendingDeleteId(null));
  };

  return (
    <section className="bg-surface-lowest border border-surface-container rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-container">
        <div className="flex items-center gap-2">
          <FaUsers className="text-primary" />
          <h2 className="font-semibold text-on-primary-fixed-var">Family Members</h2>
        </div>
        <button type="button" onClick={openCreate} className="text-sm font-medium text-primary hover:text-primary-container">
          + Add Dependent
        </button>
      </div>

      <div className="px-5 py-3 space-y-3">
        {error && <p className="text-sm text-red-500">{error}</p>}
        {loading && <LoadingState size="sm" message="Loading…" className="py-6" />}
        {!loading && members.length === 0 && (
          <p className="text-sm text-on-surface-variant">No family members added yet.</p>
        )}
        {members.map((m) => (
          <div key={m._id} className="flex items-center justify-between gap-2 py-3 border-b border-surface-container last:border-0">
            <button type="button" onClick={() => openView(m)} className="text-left flex-1 min-w-0">
              <p className="font-medium text-on-primary-fixed-var break-words">{m.fullName}</p>
              <p className="text-sm text-on-surface-variant break-words">
                {m.relation} • Dependent{getContactNumber(m) ? ` • ${getContactNumber(m)}` : ""}
              </p>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => { setSelectedMember(m); setModalMode("edit"); setModalOpen(true); }} className="p-2.5 rounded-full text-on-surface-variant hover:text-primary hover:bg-surface-low" aria-label="Edit">
                <FaEdit size={16} />
              </button>
              <button type="button" onClick={() => setPendingDeleteId(m._id)} className="p-2.5 rounded-full text-on-surface-variant hover:text-red-500 hover:bg-red-50" aria-label="Remove">
                <FaTrash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <FamilyMemberModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        member={selectedMember}
        isPrimary={false}
        staffId={staffId}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={!!pendingDeleteId}
        title="Remove family member?"
        message="This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </section>
  );
};

export default ProfileFamilySection;
